// Ineligible results on the pricing engine page (plan ppe-simplify-epps.md,
// work packages 0 and 6). Since work package 3 the calculator gives every
// reason a reasonCode and a plain-words message; the page uses them when they
// are there and falls back to matching the text (a calculator without work
// package 3). Every pattern below was checked against the reasons the
// production calculator returned for the 107 real searches of 10-26..11-02.
import * as com from "../../Common";
import { armTermLabel, noArmProductsMessage } from "./armTerms";

export const REASON_CATEGORY_LABELS = {
  dti: "Debt-to-income over the maximum",
  doc: "Income documentation not accepted",
  fico: "Credit score below the minimum",
  ltv: "LTV / CLTV over the maximum",
  amount: "Loan amount out of range",
  cit: "Citizenship",
  fthb: "First-time home buyer",
  occ: "Occupancy or property type",
  purpose: "Loan purpose",
  loc: "State or county",
  reserves: "Reserves",
  aus: "AUS type",
  lien: "Second-lien product",
  term: "No product for this loan term",
  noprice: "No rate within the lender's price limits",
  nomatch: "No product matches this scenario",
  data: "Lender data problem on Zeitro's side",
  nodata: "No rate sheet or product data for this scenario",
  vesting: "Vesting",
  dscr: "DSCR",
  credit: "Credit history",
  cashout: "Cash-out amount",
  io: "Interest only",
  overlay: "Company overlay",
  other: "Other",
};

// Work package 3's reasonCode (GOCostCalculator/.../ppe/reasons.go) -> the
// categories above.
const REASON_CODE_CATEGORY = {
  dti: "dti",
  incomeDocumentation: "doc",
  ltv: "ltv",
  creditScore: "fico",
  loanTerm: "term",
  location: "loc",
  citizenship: "cit",
  firstTimeHomeBuyer: "fthb",
  vesting: "vesting",
  loanAmount: "amount",
  lien: "lien",
  occupancy: "occ",
  propertyType: "occ",
  purpose: "purpose",
  underwriting: "aus",
  dscr: "dscr",
  creditHistory: "credit",
  reserves: "reserves",
  cashOut: "cashout",
  interestOnly: "io",
  overlay: "overlay",
  pricing: "noprice",
  noData: "nodata",
  other: "other",
};

export function categoryOf(reasonCode, text) {
  return REASON_CODE_CATEGORY[reasonCode] || reasonCategory(text);
}

// Order matters: "Failed to get X lender data filtered by loan term:fixed40"
// is a term reason, the bare "Failed to get X lender data" is ours.
const CATEGORY_PATTERNS = [
  ["dti", /\bDTI\b/],
  ["doc", /documentation type/i],
  ["lien", /closed-end second|priced only on (HELOC|second-mortgage) requests/i],
  ["nomatch", /products after .*filter/i],
  ["fico", /\bFICO\b|credit score/i],
  ["amount", /loan amount|this product serves|this request is (conforming|high balance|jumbo)/i],
  ["ltv", /\b(H?C?LTV)\b/],
  ["cit", /citizenship/i],
  ["fthb", /first-time home buyer/i],
  ["occ", /occupancy|property type/i],
  ["purpose", /purpose type/i],
  ["loc", /not available in|\bstate\b|\bcounty\b/i],
  ["reserves", /reserves/i],
  ["aus", /underwriting method|filtered by AUS/i],
  ["term", /filtered by loan term|for loan term|has no .*\bproducts?\b/i],
  ["noprice", /no rate costs|beyond limit|no pricing for lock day/i],
  ["nomatch", /qualified prds|qualified products|products? found|did not return any results|no matched mortgage types|products after/i],
  ["data", /no current rate sheet|incorrect adjustments|empty matrices|not yet supported|failed to get .* lender data|can not find the ineligible reason/i],
];

export function reasonCategory(text) {
  const reason = String(text || "");
  const hit = CATEGORY_PATTERNS.find(([, pattern]) => pattern.test(reason));
  return hit ? hit[0] : "other";
}

const PROGRAM_LABELS = {
  conventional: "Conventional",
  fha: "FHA",
  va: "VA",
  usda: "USDA",
  nonqm: "Non-QM",
  heloc: "HELOC",
  secondMortgage: "Second Mortgage",
};

const LEGACY_ARM_LABELS = { arm3: "3-year ARM", arm5: "5-year ARM", arm7: "7-year ARM", arm10: "10-year ARM" };

export function loanTermLabel(loanTerm) {
  const term = String(loanTerm || "");
  const fixed = term.match(/^fixed(\d+)$/);
  if (fixed) return `${fixed[1]} Year Fixed`;
  return armTermLabel(term) || LEGACY_ARM_LABELS[term] || term;
}

export function loanProgramLabel(loanProgram) {
  return PROGRAM_LABELS[loanProgram] || String(loanProgram || "");
}

const sentence = (text) => {
  const s = String(text || "").trim();
  if (!s) return s;
  // "iTrust is ..." keeps its lowercase brand; "borrower ..." is capitalized.
  const capitalized = /^[a-z][a-z\s]/.test(s) ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  return /[.!?]$/.test(capitalized) ? capitalized : `${capitalized}.`;
};

const money = (value) => `$${Number(value).toLocaleString("en-US")}`;

// The calculator's wording, rewritten where it is internal ("prds", "rate
// costs", "matrices"). Reasons that already read well are kept as they are.
export function friendlyReason(text) {
  const reason = String(text || "").trim();
  let m;
  if (/documentation type is not supported/i.test(reason)) return "Does not accept this income documentation.";
  if (/no rate costs|beyond limit/i.test(reason)) return "No rate within the lender's price limits.";
  if ((m = reason.match(/no pricing for lock day (\d+)/i))) return `No price for a ${m[1]}-day lock.`;
  if (/no current rate sheet/i.test(reason)) return "No current rate sheet (lender data on Zeitro's side).";
  if ((m = reason.match(/minium loan amount is ([\d.]+)/i))) return `Below the minimum loan amount (${money(m[1])}).`;
  if (/^closed-end second: priced only on second-mortgage requests/i.test(reason)) return "Closed-end second: priced only when the loan program is Second Mortgage.";
  if ((m = reason.match(/^(.*HELOC): priced only on HELOC requests/i))) return `${m[1]}: priced only when the loan program is HELOC.`;
  if ((m = reason.match(/filtered by loan term:\s*(\w+)/i))) return `No ${loanTermLabel(m[1])} products.`;
  if ((m = reason.match(/filtered by AUS:\s*(\w+)/i))) return `No products for AUS type ${m[1]}.`;
  if (/underwriting method is ineligible/i.test(reason)) return "Does not accept the selected AUS type.";
  if (/qualified prds|qualified products|products found|did not return any results|products after/i.test(reason)) return "No product matches this scenario.";
  if (/no matched mortgage types/i.test(reason)) return "No product of this loan program.";
  if (/^incorrect adjustments$/i.test(reason)) return "Price adjustments could not be applied (lender data problem on Zeitro's side).";
  if ((m = reason.match(/empty matrices for \[(.*)\]/i))) return `No eligibility rules loaded for ${m[1]} (lender data problem on Zeitro's side).`;
  if (/failed to get .* lender data$/i.test(reason)) return "Lender data is not available right now (on Zeitro's side).";
  if (/not yet supported by the pricing engine/i.test(reason)) return "This program is not priced by Zeitro yet.";
  if (/can not find the ineligible reason/i.test(reason)) return "The lender gave no reason.";
  return sentence(reason);
}

// HELOC and closed-end second products are not part of a first-lien search
// (plan 6.0). The calculator often rejects them for another reason first
// (income documentation, loan amount), so look at the product name too.
const SECOND_LIEN_NAME = /\bHELOC\b|\bHELOAN\b|closed[\s-]+end[\s-]+second|(?:^|[\s(/-]|i|EZ)CES\b|second lien|second only/i;

export function isSecondLienProduct(productName, reason, reasonCode) {
  return reasonCode === "lien" || reasonCategory(reason) === "lien" || SECOND_LIEN_NAME.test(String(productName || ""));
}

export function noProductSentence({ loanProgram, loanTerm } = {}) {
  const parts = [loanTermLabel(loanTerm), loanProgramLabel(loanProgram)].filter(Boolean);
  return `No ${parts.join(" ")} product.`;
}

const isLenderSummary = (reason) => reasonCategory(reason) === "nomatch";

// buildIneligibleByLender turns the engine response into what the Ineligibles
// tab shows, keyed by the calculator's internal lender key:
// - connected lenders only (or just `onlyLender`, the GMCC Celebrity page);
// - first-lien searches drop HELOC / closed-end second products;
// - a lender with nothing priced and nothing said gets "No <term> <program>
//   product." instead of "No reason reported." (D14, page part);
// - a priced lender with nothing ineligible is left out.
export function buildIneligibleByLender(response, scenario = {}) {
  const reasonsByLender = (response && response.ineligible_reasons) || {};
  const connected = (response && response.connected_lender_map) || {};
  const priced = new Set(((response && response.result) || [])
    .filter((lender) => lender && (lender.ProductRatesCosts || []).length > 0)
    .map((lender) => lender.Lender));
  const firstLien = scenario.lienPosition !== "second";
  const lenders = scenario.onlyLender
    ? [scenario.onlyLender]
    : Object.keys(connected).filter((key) => connected[key]);

  const out = {};
  lenders.forEach((lender) => {
    const raw = reasonsByLender[lender] || {};
    const products = (raw.ProductLevelReasons || [])
      .filter((p) => !(firstLien && isSecondLienProduct(p.ProductName, p.IneligibleReason, p.reasonCode)))
      .map((p) => ({
        ...p,
        IneligibleReason: p.message || friendlyReason(p.IneligibleReason),
        category: categoryOf(p.reasonCode, p.IneligibleReason),
      }));
    let lenderReason = String(raw.LenderLevelReason || "").trim();
    let lenderCategory = lenderReason ? categoryOf(raw.lenderLevelReasonCode, lenderReason) : "";
    // "No X qualified products found" restates the product list below it.
    if (products.length > 0 && isLenderSummary(lenderReason)) {
      lenderReason = "";
      lenderCategory = "";
    }
    if (!lenderReason && products.length === 0) {
      if (priced.has(lender)) return;
      lenderReason = noProductSentence(scenario);
      lenderCategory = "term";
    } else if (lenderReason) {
      lenderReason = raw.lenderLevelMessage || friendlyReason(lenderReason);
    }
    out[lender] = {
      LenderLevelReason: lenderReason,
      LenderLevelCategory: lenderCategory,
      ProductLevelReasons: products,
    };
  });
  return out;
}

// The one line shown when no connected lender has an eligible product
// (plan 6.6): the most common reasons instead of "No products match".
export function zeroResultSentence(response, ineligibleByLender, scenario = {}) {
  const lenders = Object.keys(ineligibleByLender || {});
  if (lenders.length === 0) return "";

  const connected = (response && response.connected_lender_map) || {};
  const rawConnected = {};
  Object.keys((response && response.ineligible_reasons) || {}).forEach((key) => {
    if (connected[key]) rawConnected[key] = response.ineligible_reasons[key];
  });
  const armMessage = noArmProductsMessage(scenario.loanTerm, rawConnected);
  if (armMessage) return armMessage;

  const counts = {};
  lenders.forEach((lender) => {
    const entry = ineligibleByLender[lender];
    if (entry.ProductLevelReasons.length > 0) {
      entry.ProductLevelReasons.forEach((p) => {
        counts[p.category] = (counts[p.category] || 0) + 1;
      });
    } else if (entry.LenderLevelCategory) {
      counts[entry.LenderLevelCategory] = (counts[entry.LenderLevelCategory] || 0) + 1;
    }
  });
  const top = Object.keys(counts)
    .sort((a, b) => counts[b] - counts[a])
    .slice(0, 3)
    .map((category) => {
      const label = category === "term" ? noProductSentence(scenario).replace(/\.$/, "") : REASON_CATEGORY_LABELS[category];
      return `${label} (${counts[category]})`;
    });

  const who = lenders.length === 1
    ? `${com.formatLenderDisplayName(lenders[0])} has no eligible product for this search.`
    : `None of your ${lenders.length} connected lenders has an eligible product for this search.`;
  return `${who} Main reasons: ${top.join("; ")}. Each lender's reasons are under Ineligibles.`;
}

// Rate sheet date for a lender's result, from FileInfo (display only; the
// calculator's own rateSheetDate replaces this in work package 9): the date
// folder of the rate sheet file (".../<Lender>/terms/20261102/1/..."), else
// the date the calculator's data was last updated (UpdatedAt, UTC).
export function rateSheetDate(fileInfo) {
  const info = fileInfo || {};
  const folder = String(info.TermsUrl || "").match(/\/terms\/(\d{4})(\d{2})(\d{2})\//);
  if (folder) return `${folder[1]}-${folder[2]}-${folder[3]}`;
  const updated = String(info.UpdatedAt || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (updated && updated[1] !== "0001") return `${updated[1]}-${updated[2]}-${updated[3]}`;
  return "";
}

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Older than the previous business day (weekends skipped, holidays not).
export function isRateSheetStale(date, today = new Date()) {
  if (!date) return false;
  const previous = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  do {
    previous.setDate(previous.getDate() - 1);
  } while (previous.getDay() === 0 || previous.getDay() === 6);
  return date < isoDate(previous);
}

export function formatRateSheetDate(date) {
  const m = String(date || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
}
